// The life of a notice before it reaches a family.
//
// Teachers organise activities and staff draft notices, but neither should be able to put a letter
// in front of every parent on their own say-so. A notice is therefore drafted, seen by the unit that
// owns it, and signed off by the principal -- or by whoever the principal hands the routine ones to.
//
// Two stages, school-wide. Not a configurable chain of N: every school described the same two, and a
// chain that can be any length is a chain somebody has to maintain.
export const STATUS = {
  DRAFT: 'draft',
  PENDING_FIRST: 'pending_first',
  PENDING_FINAL: 'pending_final',
  APPROVED: 'approved',
  REJECTED: 'rejected',
};

export const STATUS_LABEL = {
  [STATUS.DRAFT]: 'Draft',
  [STATUS.PENDING_FIRST]: 'Awaiting first approval',
  [STATUS.PENDING_FINAL]: 'Awaiting final approval',
  [STATUS.APPROVED]: 'Approved',
  [STATUS.REJECTED]: 'Sent back',
};

export const STATUS_COLOUR = {
  [STATUS.DRAFT]: 'var(--color-text-muted)',
  [STATUS.PENDING_FIRST]: 'var(--color-warning, #8a5a0b)',
  [STATUS.PENDING_FINAL]: 'var(--color-warning, #8a5a0b)',
  [STATUS.APPROVED]: 'var(--color-success, #2f6b45)',
  [STATUS.REJECTED]: 'var(--color-danger)',
};

// Both stages always apply. There is no "off" and no skipping the first one: a notice that only
// the principal saw is exactly the situation the school asked to prevent, and a switch to disable
// approval would be found and used the first week somebody was in a hurry.
//
// A stage with nobody named falls to the system administrators, who can always approve. That keeps a
// notice from ever being stuck with no possible approver, and it is visible in the settings page
// rather than silent.
export const stageFor = (settings, { email }) => {
  const a = (settings || {}).approval || {};
  const named = (stage) => ((a[stage] || {}).staff || []).map((e) => String(e).toLowerCase());
  const me = String(email || '').toLowerCase();
  return { first: named('first').includes(me), final: named('final').includes(me) };
};

export const stageIsEmpty = (settings, stage) =>
  (((settings || {}).approval || {})[stage] || {}).staff?.length ? false : true;

// Every notice starts at the first stage. Always.
export const firstPendingStatus = () => STATUS.PENDING_FIRST;

// One entry per thing that happened, appended and never rewritten.
//
// Module admins may edit a notice after it has been approved -- the school office fixing a typo
// should not have to send it back to the teacher who drafted it. That convenience costs something:
// "approved" stops meaning "this exact text was approved". Logging the edit is what keeps the record
// honest about it, so the log records edits as well as decisions.
export const logEntry = ({ action, byEmail, byName, stage, note }) => ({
  action,
  byEmail: String(byEmail || '').toLowerCase(),
  byName: byName || '',
  stage: stage || '',
  note: note || '',
  at: new Date().toISOString(),
});
